import {
  Component,
  computed,
  effect,
  ElementRef,
  HostBinding,
  input,
  output,
  Renderer2,
  signal,
  TemplateRef,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateModule } from '@ngx-translate/core';

import { BreakpointsService } from '@smw/utils-front';

import { StepState, StepType } from './step';
import { ButtonComponent, FABComponent } from '../../buttons';
import { Icon } from '../../icon';
import { TooltipDirective } from '../../tooltip/tooltip.directive';

@Component({
  selector: 'smw-step',
  templateUrl: 'step.component.html',
  styleUrls: ['./step.component.less'],
  imports: [TranslateModule, ButtonComponent, FABComponent, TooltipDirective],
})
export class StepComponent {
  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private breakpointsService: BreakpointsService,
  ) {}

  /** Identifier of the step */
  step = input.required<string>();
  /** Expect an emoji code */
  icon = input.required<string>();
  title = input.required<string>();
  summary = input.required<string>();
  /**
   * * disabled: the step is not available yet.
   * * todo: the step has not been visited yet.
   * * done: the step has been visited at least once.
   */
  state = input.required<StepState>();
  /**
   * * embed: the content is displayed within the step component.
   * * empty: no content
   */
  type = input.required<StepType>();
  ctaIcon = input<Icon>();
  ctaLabel = input<string>(marker('see'));
  ctaTooltip = input<string | TemplateRef<unknown> | undefined>();
  /** If true, CTA is displayed. */
  ctaAllowed = input<boolean>(true);
  /** If true, CTA action will do nothing, even if the state is todo or done. */
  ctaDisabled = input<boolean>();
  /**
   * If true, the CTA will be displayed as FAB on small screen
   * @default false
   */
  enableFabCta = input<boolean>(false);

  /** The step has been clicked on */
  byClick = output<void>();

  editIcon = Icon.edit;
  viewIcon = Icon.carretRight;
  closeMobileViewIcon = Icon.back;

  setId = effect(() => {
    this.renderer.setAttribute(this.elementRef.nativeElement, 'id', this.step());
  });

  isDisabled = computed(() => this.state() === 'disabled' || this.ctaDisabled() || false);
  isEmpty = computed(() => this.type() === 'empty');
  onSmallScreen = toSignal(this.breakpointsService.matches(['mobile', 'sm']));

  shouldDisplayCta = computed(
    () => !this.isDisabled() && !this.onSmallScreen() && this.ctaAllowed(),
  );
  preparedCtaIcon = computed(() => {
    const icon = this.ctaIcon();
    if (icon) {
      return icon;
    }
    return this.isEmpty() ? this.viewIcon : this.editIcon;
  });

  showContent = signal(false);
  shouldDisplayContent = computed(() => {
    const type = this.type();
    if (type === 'empty') {
      return false;
    }

    if (this.state() === 'done') {
      return this.onSmallScreen() ? this.showContent() : true;
    }

    return false;
  });

  onClick(): void {
    if (this.state() === 'disabled') {
      return;
    }

    if (this.type() === 'embed' && this.onSmallScreen()) {
      this.showContent() ? this.emitByClick() : this.showContent.set(true);
    } else {
      this.emitByClick();
    }
  }

  private emitByClick(): void {
    if (this.ctaDisabled()) {
      return;
    }

    this.byClick.emit();
  }

  @HostBinding('class') get class(): string {
    return this.state();
  }
}
